import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, CodeBlock, Code, PropTable } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is BUZZ?', level: 2 as const },
  { id: 'publish', title: 'Publishing events', level: 2 as const },
  { id: 'event-fields', title: 'Event fields', level: 2 as const },
  { id: 'built-in', title: 'Built-in event types', level: 2 as const },
]

export function EventsOverview() {
  return (
    <Article toc={toc}>
      <H1>Events — BUZZ</H1>
      <Lead>BUZZ is Rabbit's event bus. Services publish events here; subscribers receive them via webhook callbacks in real time.</Lead>

      <H2 id="what">What is BUZZ?</H2>
      <P>BUZZ decouples services by providing a shared message channel. Instead of Service A calling Service B directly, A publishes an event to BUZZ and B subscribes to it. This means:</P>
      <UL>
        <LI>Services don't need to know about each other's internals</LI>
        <LI>New consumers can be added without changing the producer</LI>
        <LI>Events are persisted — late subscribers can still catch up</LI>
      </UL>
      <Callout type="info">BUZZ is available over REST (<Code>http://localhost:3000</Code>) and gRPC (port <Code>50051</Code>) for high-throughput use cases.</Callout>

      <H2 id="publish">Publishing events</H2>
      <CodeBlock language="bash" title="Publish an event via REST">
{`POST /api/events
Authorization: Bearer <token>
Content-Type: application/json

{
  "type": "order.created",
  "payload": { "orderId": "abc-123", "amount": 199.99 },
  "ttl": 3600
}`}
      </CodeBlock>
      <P>BUZZ stores the event and immediately pushes it to all matching subscribers. The <Code>ttl</Code> field (seconds) controls when the event is garbage-collected.</P>

      <H2 id="event-fields">Event fields</H2>
      <PropTable props={[
        { name: 'type', type: 'string', required: true, description: 'Dot-namespaced event identifier, e.g. email.received or document.created.' },
        { name: 'payload', type: 'object', description: 'Arbitrary JSON payload delivered to subscribers.' },
        { name: 'ttl', type: 'number', default: '86400', description: 'Time-to-live in seconds. Event is deleted after expiry.' },
        { name: 'metadata', type: 'object', description: 'Optional key-value bag for tracing or correlation IDs.' },
      ]} />

      <H2 id="built-in">Built-in event types</H2>
      <UL>
        <LI><Code>email.received</Code> — emitted by Mail Service when a new email arrives in a monitored mailbox</LI>
        <LI><Code>document.created</Code> — emitted by Storage Service when a file is uploaded</LI>
        <LI><Code>document.updated</Code> — emitted by Storage Service when a file is replaced</LI>
      </UL>
      <P>You can define any custom event types your workflows need. There is no schema registry — event types are free-form strings.</P>
    </Article>
  )
}
